import { defineElement, FC, onDidLoad, useProp } from '@atomify/hooks';
import { Fragment, h } from '@atomify/jsx';
import { Button, ButtonInterface } from '@atoms/button/button';
import { ExtendedProjectPhase } from '@bpd-library/utilities';
import { areaSubscriptionStore } from '@features/area-subscription/store';
import {
    AreaSubscriptionStatus,
    AreaSubscriptionTriggerSource,
} from '@features/area-subscription/types';
import { isProjectPhaseSubscribable } from '@features/area-subscription/utilities';
import { isSubscribedToAreaProject, isSubscribedToProjectPhase } from '@store/subscription/store';
import { translateItem, useAreaData } from '@utilities/hooks';
import { useAreaSubscriptionLoader } from '@utilities/hooks/use-area-subscription-loader';

const SUBSCRIBE_TO_AREA_KEY = 'AreaSubscription.Trigger.SubscribeToArea';
const SUBSCRIBE_TO_PROJECTS_KEY = 'AreaSubscription.Trigger.AreaSubscribed.SubscribeToProjects';
const SUBSCRIBE_TO_MORE_PROJECTS_KEY =
    'AreaSubscription.Trigger.AreaSubscribed.SubscribeToMoreProjects';
const SUBSCRIBED_TO_PROJECTS_KEY = 'cta_SubscribedToProjectPhase';

const ctaTranslationKeysForStatus: Partial<Record<AreaSubscriptionStatus, string>> = {
    notSubscribedToArea: SUBSCRIBE_TO_AREA_KEY,
    userNotSubscribedToArea: SUBSCRIBE_TO_AREA_KEY,
    notSubscribedToAreaNoProjectsAvailable: SUBSCRIBE_TO_AREA_KEY,
    userNotSubscribedToAreaNoProjectsAvailable: SUBSCRIBE_TO_AREA_KEY,
    subscribedToAreaNotSubscribedToProjects: SUBSCRIBE_TO_PROJECTS_KEY,
    subscribedToAreaSubscribedToSomeProjects: SUBSCRIBE_TO_MORE_PROJECTS_KEY,
};

const DEFAULT_BUTTON_PROPS: Partial<ButtonInterface> = {
    width: 'full-width-mobile',
};

const AreaSubscriptionCta: FC = ({ element }) => {
    const [hook] = useProp<string | null>('hook', null);
    const [defaultLabel] = useProp<string | null>('defaultLabel', null);
    const [selectedProjectPhases] = useProp<string[] | null>('projectPhaseIds', null);
    const [source] = useProp<AreaSubscriptionTriggerSource>('source', 'content');

    const subscriptionFlow = useAreaSubscriptionLoader();
    let button: HTMLButtonElement | null | undefined;
    let projectPhases: ExtendedProjectPhase[] | undefined;
    let areaOnly = false;

    let buttonProps: Partial<ButtonInterface> = { ...DEFAULT_BUTTON_PROPS };
    if (element.dataset.button) {
        const buttonData = JSON.parse(element.dataset.button) as Partial<ButtonInterface>;
        buttonProps = { ...buttonProps, ...buttonData };
    }

    onDidLoad(async () => {
        if (selectedProjectPhases?.length) {
            const data = await useAreaData();
            projectPhases = data.projectPhases;
            projectPhases = projectPhases.filter(({ id }) => selectedProjectPhases.includes(id));
            areaOnly = !projectPhases.some(isProjectPhaseSubscribable);
        }

        await subscriptionFlow.load();
        await render();
    });

    areaSubscriptionStore.subscribe(render, ['status']);

    const isAlreadySubscribed = () => {
        if (areaOnly) {
            return isSubscribedToAreaProject();
        } else if (selectedProjectPhases?.length) {
            return selectedProjectPhases.every(isSubscribedToProjectPhase);
        } else return false;
    };

    async function handleButtonClick() {
        await subscriptionFlow.element.current?.openInitialized({
            source,
            selectedProjectPhases,
            areaOnly,
        });
    }

    async function render() {
        let buttonLabel: string;
        const { status } = areaSubscriptionStore.getState();
        const translationKey = status && ctaTranslationKeysForStatus[status];

        if (hook) {
            button = element.querySelector<HTMLButtonElement>(`[js-hook-${hook}]`);
            if (!button) return;

            button.removeEventListener('click', handleButtonClick);
            button.addEventListener('click', handleButtonClick);

            const renderEmptyState = () => {
                element.innerHTML = '';
                element.append(
                    <span class="u-inline-block u-margin-left-1 u-color--grey-50">
                        {buttonLabel}
                    </span>,
                );
            };

            if (isAlreadySubscribed()) {
                buttonLabel = await translateItem(SUBSCRIBED_TO_PROJECTS_KEY);
                renderEmptyState();
            }

            return;
        }

        element.innerHTML = '';
        if (isAlreadySubscribed()) {
            removeCalloutParent();
            return;
        }

        switch (status) {
            case undefined: {
                if (!defaultLabel) return;
                buttonLabel = defaultLabel;
                break;
            }
            case 'notSubscribedToArea':
            case 'userNotSubscribedToArea':
            case 'notSubscribedToAreaNoProjectsAvailable':
            case 'userNotSubscribedToAreaNoProjectsAvailable': {
                if (defaultLabel) {
                    buttonLabel = defaultLabel;
                } else if (translationKey) {
                    buttonLabel = await translateItem(translationKey);
                } else {
                    return;
                }
                break;
            }
            case 'subscribedToAreaNoProjectsAvailable':
            case 'subscribedToAreaSubscribedToAllProjects': {
                removeCalloutParent();
                return;
            }
            default: {
                if (!translationKey) return;
                buttonLabel = await translateItem(translationKey);
                break;
            }
        }

        element.append(<Button {...buttonProps} label={buttonLabel} onClick={handleButtonClick} />);
    }

    function removeCalloutParent() {
        const calloutElement = element.closest('[js-hook-subscription-callout]');
        if (!calloutElement) return;

        setTimeout(() => {
            calloutElement.outerHTML = '';
        }, 50);
    }

    if (hook) return <Fragment></Fragment>;

    // this button only serves as a loader element until the area-subscription component loads.
    return (
        <Button
            {...buttonProps}
            label={defaultLabel}
            hasLoading
            classes="area-subscription-cta--loader is-loading"
        />
    );
};

AreaSubscriptionCta.props = {
    defaultLabel: {
        type: String,
        reflectToAttr: true,
    },
    hook: {
        type: String,
        reflectToAttr: true,
    },
    projectPhaseIds: {
        type: Object,
        reflectToAttr: true,
    },
    source: {
        type: String,
        reflectToAttr: true,
    },
};

defineElement('area-subscription-cta', AreaSubscriptionCta);
